import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useWeb3 } from '../context/Web3Context';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { PageHero } from '../components/PageHero';
import { Trophy, Send, MousePointerClick, Target, Coins, Crown, Star, Award, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const TOP3_COLORS = ['#FFD700', '#C0C0C0', '#CD7F32'];

export default function SocialLeaderboardPage() {
    const { t } = useTranslation();
    const { address } = useWeb3();
    const [leaderboard, setLeaderboard] = useState([]);
    const [stats, setStats] = useState(null);
    const [me, setMe] = useState(null);
    const [tiers, setTiers] = useState([]);
    const [minting, setMinting] = useState(false);

    const load = async () => {
        try {
            const [lb, s, t] = await Promise.all([
                fetch(`${API}/api/social-sharing/leaderboard?limit=50`).then(r => r.json()),
                fetch(`${API}/api/social-sharing/stats`).then(r => r.json()),
                fetch(`${API}/api/social-sharing/tiers`).then(r => r.json()),
            ]);
            setLeaderboard(lb.leaderboard || []);
            setStats(s);
            setTiers(t.tiers || []);
            if (address) {
                const m = await fetch(`${API}/api/social-sharing/me/${address}`).then(r => r.json());
                setMe(m);
            }
        } catch (e) { console.error(e); }
    };

    useEffect(() => { load(); const i = setInterval(load, 30000); return () => clearInterval(i); }, [address]);

    const mintBadge = async () => {
        if (!address) return;
        setMinting(true);
        try {
            const res = await fetch(`${API}/api/social-sharing/mint-badge`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ address }),
            });
            const d = await res.json();
            if (d.success) toast.success(`Badge ${d.badge?.tier_label} minté !`);
            else toast.error(d.error || 'Erreur');
            load();
        } catch (e) { toast.error('Erreur mint'); }
        setMinting(false);
    };

    return (
        <div className="min-h-screen" data-testid="social-leaderboard-page">
            <PageHero
                icon={Crown}
                title={t('socialLb.title')}
                highlight={t('socialLb.highlight')}
                subtitle={t('socialLb.subtitle')}
            />
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">

                {/* Stats hero */}
                {stats && (
                    <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 mb-8">
                        <Card className="bg-white/5 border-white/10"><CardContent className="p-4"><div className="text-xs text-white/50 uppercase flex items-center gap-1"><Send className="w-3 h-3" />Partages</div><div className="text-2xl font-bold text-[#00FFFF]" data-testid="total-shares">{stats.total_shares}</div></CardContent></Card>
                        <Card className="bg-white/5 border-white/10"><CardContent className="p-4"><div className="text-xs text-white/50 uppercase flex items-center gap-1"><MousePointerClick className="w-3 h-3" />Clics ref</div><div className="text-2xl font-bold text-white">{stats.total_clicks}</div></CardContent></Card>
                        <Card className="bg-white/5 border-white/10"><CardContent className="p-4"><div className="text-xs text-white/50 uppercase flex items-center gap-1"><Target className="w-3 h-3" />Conversions</div><div className="text-2xl font-bold text-[#00FF66]">{stats.total_conversions}</div></CardContent></Card>
                        <Card className="bg-white/5 border-white/10"><CardContent className="p-4"><div className="text-xs text-white/50 uppercase">Influenceurs actifs</div><div className="text-2xl font-bold text-white">{stats.total_influencers}</div></CardContent></Card>
                        <Card className="bg-[#FFD700]/5 border-[#FFD700]/20"><CardContent className="p-4"><div className="text-xs text-white/50 uppercase flex items-center gap-1"><Coins className="w-3 h-3" />Payé en B4P</div><div className="text-2xl font-bold text-[#FFD700]">{stats.total_rewards_paid_b4p?.toLocaleString()}</div></CardContent></Card>
                    </div>
                )}

                {/* My rank + Tier card */}
                {address && me && (
                    <Card className="bg-gradient-to-br from-[#9B6FE8]/20 to-black border-[#9B6FE8]/30 mb-8" data-testid="my-social-stats">
                        <CardContent className="p-5">
                            <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
                                <div className="flex items-center gap-4">
                                    <div className="text-4xl font-black text-[#9B6FE8]">#{me.rank || '-'}</div>
                                    <div>
                                        <div className="text-white font-mono text-sm">{address.slice(0, 10)}...{address.slice(-4)}</div>
                                        <div className="flex flex-wrap gap-2 mt-1">
                                            {me.referral_code && <Badge className="bg-[#FFD700]/15 text-[#FFD700] border-[#FFD700]/30">Code: {me.referral_code}</Badge>}
                                            {me.tier_info && (
                                                <Badge style={{ backgroundColor: `${me.tier_info.color}20`, color: me.tier_info.color, borderColor: `${me.tier_info.color}50` }} className="font-bold">
                                                    <Award className="w-3 h-3 mr-1" />
                                                    {me.tier_info.label} ×{me.tier_info.multiplier}
                                                </Badge>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <div className="grid grid-cols-4 gap-4 text-center">
                                    <div><div className="text-xl font-bold text-[#00FFFF]">{me.total_shares || 0}</div><div className="text-[10px] uppercase text-white/40">Partages</div></div>
                                    <div><div className="text-xl font-bold text-white">{me.total_clicks || 0}</div><div className="text-[10px] uppercase text-white/40">Clics</div></div>
                                    <div><div className="text-xl font-bold text-[#00FF66]">{me.total_conversions || 0}</div><div className="text-[10px] uppercase text-white/40">Conv.</div></div>
                                    <div><div className="text-xl font-bold text-[#FFD700]">{(me.total_rewards_b4p || 0).toFixed(1)}</div><div className="text-[10px] uppercase text-white/40">B4P</div></div>
                                </div>
                            </div>

                            {/* Tier progress bar */}
                            {me.tier_info && me.tier_info.next_tier && (
                                <div className="mb-3">
                                    <div className="flex justify-between text-xs text-white/60 mb-1">
                                        <span>Progression vers <strong style={{ color: me.tier_info.next_tier.color }}>{me.tier_info.next_tier.label} ×{me.tier_info.next_tier.multiplier}</strong></span>
                                        <span>{me.total_conversions || 0} / {me.tier_info.next_tier.min_conversions} conversions</span>
                                    </div>
                                    <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                                        <div className="h-full transition-all" style={{ width: `${me.tier_info.progress_percent}%`, background: `linear-gradient(to right, ${me.tier_info.color}, ${me.tier_info.next_tier.color})` }} />
                                    </div>
                                    <div className="text-xs text-white/40 mt-1">Encore {me.tier_info.conversions_to_next} conversions pour débloquer le palier suivant.</div>
                                </div>
                            )}

                            {/* Mint badge button */}
                            {me.tier_info?.nft_eligible && (
                                <div className="flex flex-wrap items-center gap-3 pt-3 border-t border-white/10">
                                    {(me.badges || []).find(b => b.tier === me.tier_info.id) ? (
                                        <Badge className="bg-[#00FF66]/15 text-[#00FF66] border-[#00FF66]/30">
                                            <Sparkles className="w-3 h-3 mr-1" />
                                            Badge {me.tier_info.label} minté
                                        </Badge>
                                    ) : (
                                        <Button onClick={mintBadge} disabled={minting} className="font-bold" style={{ backgroundColor: me.tier_info.color, color: '#000' }} data-testid="mint-badge-btn">
                                            <Award className="w-4 h-4 mr-2" />
                                            {minting ? 'Mint en cours...' : `Mint badge ${me.tier_info.label}`}
                                        </Button>
                                    )}
                                    <span className="text-xs text-white/50">{me.tier_info.perks}</span>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                )}

                {/* Tier ladder */}
                {tiers.length > 0 && (
                    <Card className="bg-white/5 border-white/10 mb-8" data-testid="tier-ladder">
                        <CardHeader><CardTitle className="text-white flex items-center gap-2"><Award className="w-5 h-5 text-[#FFD700]" />Carrière Influenceur</CardTitle></CardHeader>
                        <CardContent>
                            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                                {tiers.map(t => {
                                    const isMine = me?.tier_info?.id === t.id;
                                    return (
                                        <div key={t.id} className={`p-3 rounded-lg border transition-all ${isMine ? 'bg-white/10' : 'bg-black/30 border-white/5'}`} style={isMine ? { borderColor: t.color } : {}}>
                                            <div className="flex items-center gap-2 mb-1">
                                                <Award className="w-4 h-4" style={{ color: t.color }} />
                                                <span className="font-bold" style={{ color: t.color }}>{t.label}</span>
                                                {isMine && <Badge className="text-[10px]" style={{ backgroundColor: `${t.color}30`, color: t.color }}>Vous</Badge>}
                                            </div>
                                            <div className="text-xs text-white/50">≥ {t.min_conversions} conversions</div>
                                            <div className="text-sm font-bold mt-1" style={{ color: t.color }}>×{t.multiplier} B4P</div>
                                            {t.nft_eligible && <div className="text-[10px] text-white/40 mt-1">+ NFT badge</div>}
                                        </div>
                                    );
                                })}
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* Leaderboard */}
                <Card className="bg-white/5 border-white/10">
                    <CardHeader>
                        <CardTitle className="text-white flex items-center gap-2"><Trophy className="w-5 h-5 text-[#FFD700]" />Classement</CardTitle>
                    </CardHeader>
                    <CardContent>
                        {leaderboard.length === 0 ? (
                            <div className="text-center text-white/40 py-8" data-testid="empty-leaderboard">
                                Aucun influenceur encore. Soyez le premier — partagez un pari et gagnez !
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="text-white/40 text-xs uppercase tracking-wider">
                                            <th className="text-left py-2 px-2">#</th>
                                            <th className="text-left py-2 px-2">Adresse</th>
                                            <th className="text-left py-2 px-2">Tier</th>
                                            <th className="text-left py-2 px-2">Code</th>
                                            <th className="text-right py-2 px-2"><Send className="w-3 h-3 inline" /> Partages</th>
                                            <th className="text-right py-2 px-2"><MousePointerClick className="w-3 h-3 inline" /> Clics</th>
                                            <th className="text-right py-2 px-2"><Target className="w-3 h-3 inline" /> Conv.</th>
                                            <th className="text-right py-2 px-2"><Coins className="w-3 h-3 inline" /> B4P</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {leaderboard.map((u, idx) => {
                                            const tier = u.tier_info || {};
                                            return (
                                                <tr key={u.address} className="border-t border-white/5 hover:bg-white/5 transition-colors" data-testid={`leaderboard-row-${idx}`}>
                                                    <td className="py-3 px-2">
                                                        <div className="flex items-center gap-2">
                                                            {idx < 3 ? (
                                                                <Star className="w-4 h-4" style={{ color: TOP3_COLORS[idx], fill: TOP3_COLORS[idx] }} />
                                                            ) : (
                                                                <span className="text-white/40 text-xs">#{idx + 1}</span>
                                                            )}
                                                            {idx < 3 && <span className="font-bold" style={{ color: TOP3_COLORS[idx] }}>#{idx + 1}</span>}
                                                        </div>
                                                    </td>
                                                    <td className="py-3 px-2 font-mono text-xs text-white">{u.address.slice(0, 8)}...{u.address.slice(-4)}</td>
                                                    <td className="py-3 px-2">
                                                        <div className="flex items-center gap-1">
                                                            <Badge style={{ backgroundColor: `${tier.color}20`, color: tier.color, borderColor: `${tier.color}50` }} className="text-xs">
                                                                <Award className="w-3 h-3 mr-1" />
                                                                {tier.label || 'Rookie'}
                                                            </Badge>
                                                            {u.badge_minted && <Sparkles className="w-3 h-3 text-[#00FF66]" />}
                                                        </div>
                                                    </td>
                                                    <td className="py-3 px-2">
                                                        {u.referral_code ? (
                                                            <Badge className="bg-[#FFD700]/10 text-[#FFD700] border-[#FFD700]/30 text-xs">{u.referral_code}</Badge>
                                                        ) : (
                                                            <span className="text-white/30 text-xs">-</span>
                                                        )}
                                                    </td>
                                                    <td className="py-3 px-2 text-right text-[#00FFFF] font-medium">{u.total_shares || 0}</td>
                                                    <td className="py-3 px-2 text-right text-white">{u.total_clicks || 0}</td>
                                                    <td className="py-3 px-2 text-right text-[#00FF66] font-bold">{u.total_conversions || 0}</td>
                                                    <td className="py-3 px-2 text-right text-[#FFD700] font-bold">{(u.total_rewards_b4p || 0).toFixed(1)}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </CardContent>
                </Card>

                <div className="mt-6 p-4 bg-white/5 rounded-lg border border-white/10 text-sm text-white/60 flex items-start gap-3">
                    <Send className="w-5 h-5 text-[#00FFFF] mt-0.5 flex-shrink-0" />
                    <div>
                        <strong className="text-white">Comment gagner :</strong> Chaque partage Telegram contient automatiquement votre code MLM. Quand un visiteur clique → vous touchez <strong className="text-[#FFD700]">+1 B4P</strong> (max 1 / personne / 24h). Quand ce visiteur place un pari → vous touchez <strong className="text-[#FFD700]">+5 B4P</strong> en plus, ET vous percevez vos commissions MLM 7 niveaux sur tous ses futurs achats.
                    </div>
                </div>
            </div>
        </div>
    );
}
